import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Avatar, Box, Button, CircularProgress, Grid, TextField, Typography } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { MinhaEmpresaService } from "../../service/minha_empresa_service";

const DADOS_VAZIOS = { nome: "", nome_fantasia: "", cpf_cnpj: "", telefone: "", email: "" };
const LOGO_TAMANHO_MAX_MB = 5;

function formatCpfCnpj(value) {
	const cleanValue = (value || "").replace(/\D/g, "").slice(0, 14);

	if (cleanValue.length <= 11) {
		return cleanValue
			.replace(/(\d{3})(\d)/, "$1.$2")
			.replace(/(\d{3})(\d)/, "$1.$2")
			.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
	}
	return cleanValue
		.replace(/(\d{2})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d)/, "$1/$2")
		.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

// O telefone é gravado como +55DDDNUMERO; na tela aparece sem o +55.
function formatTelefone(value) {
	let digits = (value || "").replace(/\D/g, "");
	if ((digits.length === 12 || digits.length === 13) && digits.startsWith("55")) digits = digits.slice(2);
	digits = digits.slice(0, 11);
	if (digits.length > 10) return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
	if (digits.length > 6) return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
	if (digits.length > 2) return digits.replace(/(\d{2})(\d{0,5})/, "($1) $2");
	return digits;
}

// O backend devolve o arquivo em base64, sem o tipo; o primeiro caractere entrega o formato.
function logoComoDataUrl(base64) {
	if (!base64 || typeof base64 !== "string") return null;
	const tipos = { "/": "image/jpeg", "i": "image/png", "R": "image/gif", "U": "image/webp", "P": "image/svg+xml" };
	const tipo = tipos[base64.charAt(0)] || "image/png";
	return `data:${tipo};base64,${base64}`;
}

function SuaConta({ cardSx, titleSx, fieldSx, buttonSx, onNotify }) {

	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState("");

	const [dados, setDados] = useState(DADOS_VAZIOS);
	const [dadosError, setDadosError] = useState("");
	const [salvando, setSalvando] = useState(false);

	const [logo, setLogo] = useState(null);
	const [logoError, setLogoError] = useState("");
	const [enviandoLogo, setEnviandoLogo] = useState(false);
	const inputLogoRef = useRef(null);

	const carregar = useCallback(async () => {
		setLoading(true);
		const service = new MinhaEmpresaService();
		try {
			const empresa = await service.get();
			setDados({
				nome: empresa.nome || "",
				nome_fantasia: empresa.nome_fantasia || "",
				cpf_cnpj: formatCpfCnpj(empresa.cpf_cnpj),
				telefone: formatTelefone(empresa.telefone),
				email: empresa.email || "",
			});
			setLoadError("");
		} catch (error) {
			setLoadError(error.message || "Erro ao carregar os dados da empresa.");
		} finally {
			setLoading(false);
		}

		// O logo não impede a tela: sem ele, fica o ícone.
		try {
			setLogo(logoComoDataUrl(await service.getLogo()));
		} catch {
			setLogo(null);
		}
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const handleChange = (field, value) => {
		setDados((prev) => ({ ...prev, [field]: value }));
	};

	const validar = () => {
		const cpfCnpj = dados.cpf_cnpj.replace(/\D/g, "");
		const telefone = dados.telefone.replace(/\D/g, "");
		const email = dados.email.trim();

		if (!dados.nome.trim()) return "Informe a razão social.";
		if (cpfCnpj.length !== 11 && cpfCnpj.length !== 14) return "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).";
		if (telefone.length < 10) return "Informe o telefone com DDD.";
		if (!email) return "Informe o e-mail.";
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Informe um e-mail válido.";

		return "";
	};

	const handleSalvar = async () => {
		const erro = validar();
		if (erro) {
			setDadosError(erro);
			return;
		}

		setDadosError("");
		setSalvando(true);
		try {
			const empresa = await new MinhaEmpresaService().update({
				nome: dados.nome.trim(),
				nome_fantasia: dados.nome_fantasia.trim(),
				cpf_cnpj: dados.cpf_cnpj.replace(/\D/g, ""),
				telefone: dados.telefone.replace(/\D/g, ""),
				email: dados.email.trim(),
			});
			setDados({
				nome: empresa.nome || "",
				nome_fantasia: empresa.nome_fantasia || "",
				cpf_cnpj: formatCpfCnpj(empresa.cpf_cnpj),
				telefone: formatTelefone(empresa.telefone),
				email: empresa.email || "",
			});
			onNotify("Dados da empresa atualizados com sucesso!");
		} catch (error) {
			setDadosError(error.message || "Erro ao salvar os dados da empresa.");
		} finally {
			setSalvando(false);
		}
	};

	const handleEscolherLogo = async (event) => {
		const arquivo = event.target.files?.[0];
		// Permite escolher o mesmo arquivo de novo depois de um erro.
		event.target.value = "";
		if (!arquivo) return;

		if (!arquivo.type.startsWith("image/")) {
			setLogoError("Escolha um arquivo de imagem.");
			return;
		}
		if (arquivo.size > LOGO_TAMANHO_MAX_MB * 1024 * 1024) {
			setLogoError(`A imagem deve ter no máximo ${LOGO_TAMANHO_MAX_MB} MB.`);
			return;
		}

		setLogoError("");
		setEnviandoLogo(true);
		try {
			const service = new MinhaEmpresaService();
			await service.updateLogo(arquivo);
			setLogo(logoComoDataUrl(await service.getLogo()));
			onNotify("Logo atualizado com sucesso!");
		} catch (error) {
			setLogoError(error.message || "Erro ao enviar o logo.");
		} finally {
			setEnviandoLogo(false);
		}
	};

	const handleRemoverLogo = async () => {
		if (!window.confirm("Remover o logo da empresa?")) return;

		setLogoError("");
		setEnviandoLogo(true);
		try {
			await new MinhaEmpresaService().deleteLogo();
			setLogo(null);
			onNotify("Logo removido.");
		} catch (error) {
			setLogoError(error.message || "Erro ao remover o logo.");
		} finally {
			setEnviandoLogo(false);
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress sx={{ color: "#1a9e8f" }} />
			</Box>
		);
	}

	if (loadError) {
		return (
			<Alert
				severity="error"
				action={
					<Button color="inherit" size="small" onClick={carregar}>
						Tentar novamente
					</Button>
				}
			>
				{loadError}
			</Alert>
		);
	}

	return (
		<>
			<Box sx={cardSx}>
				<Typography variant="subtitle1" sx={titleSx}>
					Logo da empresa
				</Typography>

				{logoError && (
					<Alert severity="error" sx={{ mb: 3 }}>
						{logoError}
					</Alert>
				)}

				<Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
					<Avatar
						src={logo || undefined}
						alt="Logo da empresa"
						sx={{ width: 96, height: 96, bgcolor: "#e8f5f3", color: "#1a9e8f", border: "1px solid #e0e0e0" }}
					>
						<BusinessIcon sx={{ fontSize: 44 }} />
					</Avatar>

					<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
						<input
							ref={inputLogoRef}
							type="file"
							accept="image/*"
							hidden
							onChange={handleEscolherLogo}
						/>
						<Button
							variant="contained"
							onClick={() => inputLogoRef.current?.click()}
							disabled={enviandoLogo}
							sx={buttonSx}
						>
							{enviandoLogo ? "Enviando..." : logo ? "Alterar logo" : "Enviar logo"}
						</Button>
						{logo && (
							<Button
								variant="outlined"
								color="error"
								onClick={handleRemoverLogo}
								disabled={enviandoLogo}
								sx={{ textTransform: "none", fontSize: "15px", px: 3 }}
							>
								Remover logo
							</Button>
						)}
					</Box>
				</Box>
			</Box>

			<Box sx={cardSx}>
				<Typography variant="subtitle1" sx={titleSx}>
					Dados da empresa
				</Typography>

				{dadosError && (
					<Alert severity="error" sx={{ mb: 3 }}>
						{dadosError}
					</Alert>
				)}

				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 6 }}>
						<TextField
							fullWidth
							label="Nome fantasia"
							value={dados.nome_fantasia}
							onChange={(e) => handleChange("nome_fantasia", e.target.value)}
							sx={fieldSx}
						/>
					</Grid>

					<Grid size={{ xs: 12, md: 6 }}>
						<TextField
							fullWidth
							label="Razão social *"
							value={dados.nome}
							onChange={(e) => handleChange("nome", e.target.value)}
							sx={fieldSx}
						/>
					</Grid>

					<Grid size={{ xs: 12, md: 4 }}>
						<TextField
							fullWidth
							label="CPF/CNPJ *"
							value={dados.cpf_cnpj}
							onChange={(e) => handleChange("cpf_cnpj", formatCpfCnpj(e.target.value))}
							sx={fieldSx}
						/>
					</Grid>

					<Grid size={{ xs: 12, md: 4 }}>
						<TextField
							fullWidth
							label="Telefone *"
							value={dados.telefone}
							onChange={(e) => handleChange("telefone", formatTelefone(e.target.value))}
							placeholder="(00) 00000-0000"
							sx={fieldSx}
						/>
					</Grid>

					<Grid size={{ xs: 12, md: 4 }}>
						<TextField
							fullWidth
							label="E-mail *"
							type="email"
							value={dados.email}
							onChange={(e) => handleChange("email", e.target.value)}
							placeholder="exemplo@email.com"
							sx={fieldSx}
						/>
					</Grid>
				</Grid>

				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
					<Button variant="contained" onClick={handleSalvar} disabled={salvando} sx={buttonSx}>
						{salvando ? "Salvando..." : "Salvar alterações"}
					</Button>
				</Box>
			</Box>
		</>
	);
}

export default SuaConta;
